import { useState, useContext, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Modal, FilledInput, Typography, Button, FormGroup, FormLabel, FormControl, Box, Grid, TextField, Switch, Select, MenuItem } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import themeColors from '../../assets/colors';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import AuthContext from '../../context';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeColors.background.default,
        padding: 20,
        width: '70%',
        borderRadius: 10
    },
    store: {
        marginLeft: 15,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    bottom: {
        display: 'flex',
        alignSelf: 'flex-end',
    }
}));

const defSupplier = { id: 0, name: 'Choose a supplier' };
const defLand = { id: 0, name: 'Choose a land' };
const defLocation = { id: 0, name: 'Choose a location' };

const TicketMasterCreate = ({ open, onClose }) => {

    const sede = JSON.parse(localStorage.getItem('@sede'));
    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const history = useHistory();
    const [state, setState] = useState({
        suppliers: [defSupplier],
        lands: [defLand],
        locations: [defLocation],
        ticketMaster: {
            code: '',
            date: dayjs().format('YYYY-MM-DD'),
            supplier: defSupplier,
            land: defLand,
            location: defLocation,
            is_automatic: false,
            payment: '-',
            weight_scale: 0
        }
    });

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    useEffect(() => {
        setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, weight_scale: 0 } }));
    }, [state.ticketMaster.is_automatic]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketMaster/create');
        if (response.status === 200) {
            const { suppliers, lands, locations } = response;

            setState(prev => ({ ...prev, suppliers, lands, locations }));
        } else {
            console.log('[TicketMasterCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, [target.name]: target.value } }));
    const handleAutocomplete = (key, value) => setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, [key]: value } }));

    const validateForm = () => {
        const { code, date, supplier, land, location } = state.ticketMaster;
        if (!code.trim() || !date || Number(supplier?.id) < 1 || Number(land?.id) < 1 || Number(location?.id) < 1) {
            showToast('All fields are required', 'error');
            return false;
        }

        return true;
    }

    const store = async () => {
        const cond = validateForm();
        if (cond) {
            showLoader(true);
            const response = await post('/ticketMaster/store', state.ticketMaster);
            if (response.status === 200) {
                showToast(response.message);
                onClose();
                clear();
                history.push(`/ticket/tickets/master/${response.id}`);
            } else {
                console.log('[TicketMasterCreate] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const handleSwitch = ({ target }) => {
        setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, is_automatic: target.checked } }));
        if (target.checked) {
            getAutomaticCode();
        } else {
            setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, code: '' } }));
        }
    }

    const getAutomaticCode = async () => {
        showLoader(true);
        const response = await get('/ticketMaster/automaticCode');
        if (response.status === 200) {
            setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, code: response.code } }));
        } else {
            console.log('[TicketMasterCreate] getAutomaticCode error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const clear = () => {
        setState(prev => ({
            ...prev,
            ticketMaster: {
                ...prev.ticketMaster,
                code: '',
                date: dayjs().format('YYYY-MM-DD'),
                supplier: defSupplier,
                land: defLand,
                location: defLocation,
                is_automatic: false
            }
        }));
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.root}
        >
            <div className={classes.container}>
                <Typography variant='h2'>{'Create master ticket'}</Typography>

                <Grid container>
                    <Grid item xs={6} lg={2}>
                        <FormGroup style={{ alignItems: 'center' }}>
                            <FormLabel>{'Automatic code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                                style={{ alignItems: 'center' }}
                            >
                                <Switch
                                    name='is_automatic'
                                    onChange={handleSwitch}
                                    checked={state.ticketMaster.is_automatic}
                                />
                                <FormLabel>{state.ticketMaster.is_automatic ? 'Yes' : 'No'}</FormLabel>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={2}>
                        <FormGroup>
                            <FormLabel>{'Code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Code'
                                    name='code'
                                    readOnly={state.ticketMaster.is_automatic}
                                    value={state.ticketMaster.code}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Date'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='date'
                                    placeholder='Date'
                                    name='date'
                                    value={state.ticketMaster.date}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    {
                        !state.ticketMaster.is_automatic &&
                        <Grid item xs={12} lg={2}>
                            <FormGroup>
                                <FormLabel>{'Net weight scale'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        autoComplete='off'
                                        type='number'
                                        inputProps={{ min: 0 }}
                                        placeholder='Weight'
                                        name='weight_scale'
                                        value={state.ticketMaster.weight_scale}
                                        onChange={handleChange}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    }
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Sede'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    disabled
                                    type='text'
                                    value={sede.name}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Supplier'}</FormLabel>
                            <Autocomplete
                                options={state.suppliers}
                                value={state.ticketMaster.supplier}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                onChange={(e, value) => handleAutocomplete('supplier', value)}
                                renderInput={params => <TextField {...params} placeholder='Supplier' />}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Land'}</FormLabel>
                            <Autocomplete
                                options={state.lands}
                                value={state.ticketMaster.land}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                onChange={(e, value) => handleAutocomplete('land', value)}
                                renderInput={params => <TextField {...params} placeholder='Land' />}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Location'}</FormLabel>
                            <Autocomplete
                                options={state.locations}
                                value={state.ticketMaster.location}
                                getOptionLabel={option => option?.name ?? ''}
                                getOptionSelected={(option, value) => option.id === value.id}
                                onChange={(e, value) => handleAutocomplete('location', value)}
                                renderInput={params => <TextField {...params} placeholder='Location' />}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <FormGroup>
                            <FormLabel>{'Payment'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.ticketMaster.payment}
                                    onChange={({ target }) => setState(prev => ({ ...prev, ticketMaster: { ...prev.ticketMaster, payment: target.value } }))}
                                >
                                    <MenuItem value='-'>{'Choose a payment method'}</MenuItem>
                                    <MenuItem value='check'>{'Check'}</MenuItem>
                                    <MenuItem value='cash'>{'Cash'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>

                <div className={classes.bottom}>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={onClose}
                    >
                        {'Cancel'}
                    </Button>
                    <Button
                        variant='contained'
                        className={classes.store}
                        onClick={store}
                    >
                        {'Store'}
                    </Button>
                </div>
            </div>
        </Modal>
    );

}

export default TicketMasterCreate;